/**
 * Collision Detection Types
 * Types for aircraft and hangar wall collision detection
 */

import type { DoorSpec } from '@/lib/templates/types'

/**
 * Safety margins configuration stored in locations.settings.safetyMargins
 * All values in feet
 */
export interface SafetyMarginsConfig {
  /** Clearance from wing tips to walls (default: 2.5 ft) */
  wingtipClearance: number
  /** Clearance from tail to back wall (default: 1.5 ft) */
  tailClearance: number
  /** Clearance from aircraft to door frame (default: 1.0 ft) */
  doorClearance: number
  /** Buffer added to collision volumes (default: 0.5 ft) */
  collisionBufferSize: number
}

/** Default safety margins (used when not overridden by location settings) */
export const DEFAULT_SAFETY_MARGINS: SafetyMarginsConfig = {
  wingtipClearance: 2.5,
  tailClearance: 1.5,
  doorClearance: 1.0,
  collisionBufferSize: 0.5,
}

/**
 * Wall/door collision info for a single aircraft
 */
export interface WallCollisionInfo {
  /** Which wall side was hit */
  side: 'front' | 'back' | 'left' | 'right'
  /** Unit ID containing this wall */
  unitId: string
  /** Area/Building ID */
  areaId: string
  /** Whether this is a door collision (true) or solid wall collision (false) */
  isDoor: boolean
}

/**
 * Result of a collision check operation
 */
/** Obstacle collision info for a single aircraft */
export interface ObstacleCollisionInfo {
  obstacleId: string
  obstacleName: string
}

export interface CollisionResult {
  /** Set of aircraft allocation IDs that are colliding */
  collisions: Set<string>
  /** Whether this was a partial check (proximity-based) */
  isPartialCheck: boolean
  /** IDs of aircraft that were checked */
  checkedAircraftIds: string[]
  /** Performance timing in milliseconds */
  checkDurationMs: number
  /** Wall/door collision details per aircraft */
  wallCollisions?: Map<string, WallCollisionInfo>
  /** Obstacle collision details per aircraft */
  obstacleCollisions?: Map<string, ObstacleCollisionInfo>
}

/**
 * Type of collision detected
 */
export type CollisionType = 'aircraft' | 'wall' | 'door' | 'obstacle'

/**
 * Detailed collision information for a single aircraft
 */
export interface CollisionDetails {
  /** Allocation ID of the colliding aircraft */
  allocationId: string
  /** Type of collision */
  collisionType: CollisionType
  /** IDs of other aircraft in collision (for aircraft-to-aircraft) */
  collidingAircraftIds?: string[]
  /** Which wall was hit (for wall collisions) */
  wallSide?: 'front' | 'back' | 'left' | 'right'
  /** Area/Building ID (for multi-hangar support) */
  areaId?: string
  /** Unit ID if aircraft is within a specific unit */
  unitId?: string
  /** Obstacle ID (for obstacle collisions) */
  obstacleId?: string
  /** Obstacle name for display */
  obstacleName?: string
  /** Timestamp of collision detection */
  detectedAt: number
}

/**
 * Collision details stored in allocations.collision_details JSONB
 */
export interface PersistedCollisionDetails {
  collisionType: CollisionType
  collidingWith: string[] // Aircraft IDs or wall identifiers
  detectedAt: string // ISO timestamp
  safetyMarginsUsed: SafetyMarginsConfig
}

/**
 * Hangar/Area configuration for collision volume generation
 */
export interface HangarCollisionConfig {
  /** Area/Building ID from database */
  areaId: string
  /** Exterior dimensions in feet */
  width: number // X-axis (length)
  depth: number // Z-axis
  height: number // Y-axis
  /** Wall thickness in feet */
  wallThickness: number
  /** Hangar rotation in degrees (0-360) */
  rotation: number
  /** Hangar position in feet [x, y, z] relative to map center */
  position: [number, number, number]
  /** Door configurations per wall */
  doors: {
    front: DoorSpec[]
    back: DoorSpec[]
    left: DoorSpec[]
    right: DoorSpec[]
  }
}

/**
 * Wall definition for collision volume creation
 */
export interface WallDefinition {
  /** Position offset from hangar center (in meters) */
  position: [number, number, number]
  /** Size [width, height, depth] in meters */
  size: [number, number, number]
  /** Which wall side this represents */
  side: 'back' | 'left' | 'right'
  /** Door openings to subtract from this wall */
  doors?: DoorSpec[]
}

/**
 * Debug verbosity levels
 */
export type DebugVerbosityLevel = 'BASIC' | 'DETAILED' | 'VERBOSE'

/**
 * Debug flags for specific collision aspects
 */
export interface CollisionDebugFlags {
  OBB_CREATION: boolean
  COMPONENT: boolean
  OBB_INTERSECTION: boolean
  BVH: boolean
  WALL: boolean
  HULL_FAILURE: boolean
}

/**
 * Debug thresholds
 */
export interface CollisionDebugThresholds {
  lowEfficiency: number
  closeProximity: number
}

// ============================================================================
// UNIT-BASED COLLISION TYPES (for multi-unit buildings with edges)
// ============================================================================

/**
 * Wall segment for collision detection
 * Represents a solid wall section (gap between doors or full wall if no doors)
 */
export interface WallSegment {
  /** Start position along the wall in feet (0 = wall start) */
  start_ft: number
  /** End position along the wall in feet */
  end_ft: number
}

/**
 * Door collision segment
 * Represents a door that should trigger collision detection
 */
export interface DoorCollisionSegment {
  /** Start position along the wall in feet */
  start_ft: number
  /** End position along the wall in feet */
  end_ft: number
  /** Door height in feet */
  height_ft: number
  /** Door type for collision info */
  doorType: string
}

/**
 * Edge collision configuration derived from UnitEdge
 * Used for wall/door collision detection
 */
export interface EdgeCollisionConfig {
  /** Which side of the unit (front, back, left, right) */
  side: 'front' | 'back' | 'left' | 'right'
  /** Edge type from template (exterior = solid wall, partition = shared, internal = open) */
  type: 'exterior' | 'partition' | 'internal'
  /** Total length of this edge in feet */
  length_ft: number
  /** Height of this wall in feet */
  height_ft: number
  /** Solid wall segments (gaps between these are door openings) */
  solidSegments: WallSegment[]
  /** Door openings on this edge */
  doorOpenings: DoorSpec[]
  /** Door collision segments (doors that trigger collision) */
  doorSegments?: DoorCollisionSegment[]
}

/**
 * Arch-specific collision parameters.
 * When present on UnitCollisionConfig, the collision builder generates curved
 * shell + arch-shaped end-wall meshes instead of flat rectangular boxes.
 */
export interface ArchCollisionConfig {
  span_ft: number
  length_ft: number
  crown_height_ft: number
  knee_wall_height_ft: number
  arch_profile: 'semicircular' | 'parabolic'
  arch_segments: number
  wall_thickness_ft: number
  frontDoors?: DoorSpec[]
  backDoors?: DoorSpec[]
}

/**
 * Unit-based collision configuration
 * Built from GeneratedUnit.edges[] for wall/door collision detection
 */
export interface UnitCollisionConfig {
  /** Unit code (e.g., "A-1", "B-2") */
  unitId: string
  /** Parent area/building ID */
  areaId: string
  /** Unit position in meters [x, y, z] relative to map center */
  position: [number, number, number]
  /** Interior dimensions [width, depth, height] in feet */
  dimensions: [number, number, number]
  /** Wall thickness in feet */
  wallThickness: number
  /** Building rotation in degrees */
  rotation: number
  /** Edge collision configs for each wall */
  edges: EdgeCollisionConfig[]
  /** Arch-specific collision params (replaces flat edge-based walls with curved geometry) */
  archConfig?: ArchCollisionConfig
}
